/**
 * @file: handle.ts
 *
 * A handle to the room a conversation is showing — a discriminated union
 * resolved BEFORE the conversation renders (see `resolveConversationHandle` + the
 * entry-point gates). The conversation itself never resolves; it just
 * consumes one of these:
 *
 *   - `baked` — an existing thread/room in the DB: carries the thread id,
 *     room id, type, and a resolved display title.
 *   - `bare`  — no room yet (a first chat with a peer): carries only the
 *     peer. The first send materialises the room and promotes it to baked.
 */

import { ChatRoomType } from '@euler/model/chat';
import { IdType } from '@euler/model/common';

/** The counterpart of a not-yet-created private chat. */
export interface ChatConversationPeer {
  platformUserId: IdType;
  appId: string;
  appScope: string;
  name?: string;
  avatar?: string | null;
}

export interface BakedConversationHandle {
  kind: 'baked';
  roomId: string;
  /**
   * The viewer's per-user thread in this room, or `null` when the room
   * exists but they have no thread yet (a deep-linked / freshly created room
   * the viewer hasn't interacted with). The first send materialises it. Not
   * a string sentinel — `null` IS the "no thread" state, so callers branch on
   * it honestly rather than testing for `''`.
   */
  threadId: string | null;
  roomType: ChatRoomType;
  /**
   * Resolved header title — private: the peer's name; group: the custom or
   * auto member-name title. Always a string (the resolver collapses the
   * individually-nullable source fields), '' only as a last resort.
   */
  title: string;
}

export interface BareConversationHandle {
  kind: 'bare';
  peer: ChatConversationPeer;
}

export type ChatConversationHandle =
  | BakedConversationHandle
  | BareConversationHandle;

export const isBaked = (
  id: ChatConversationHandle,
): id is BakedConversationHandle => id.kind === 'baked';

/** A baked room IS loadable (has a thread) — narrows `threadId` to string. */
export const hasThread = (
  id: ChatConversationHandle,
): id is BakedConversationHandle & { threadId: string } =>
  isBaked(id) && id.threadId != null;

export const isGroupRoom = (id: ChatConversationHandle): boolean =>
  isBaked(id) && id.roomType === 'group';

/** Header title — baked room's resolved title, or the bare peer's name. */
export const roomTitle = (id: ChatConversationHandle): string =>
  isBaked(id) ? id.title : id.peer.name ?? '';

/**
 * Stable identity for a handle — keys the desktop detail/info panes so they
 * remount on a conversation switch.
 */
export const conversationHandleKey = (
  handle: ChatConversationHandle,
): string =>
  handle.kind === 'baked'
    ? `room:${handle.roomId}`
    : `peer:${handle.peer.platformUserId}`;
